import { useEffect, useState } from "react";
import { ArrowDownRight, ArrowUpRight, Minus } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell } from "recharts";
import AppShell from "../components/AppShell";
import { EmptyData, ProGate, Spinner } from "../components/Shared";
import { MonthPicker } from "./Dashboard";
import { useAuth } from "../context/AuthContext";
import { api, gbp, signedGbp, catColor } from "../lib/api";

export default function Compare() {
    const { isPro } = useAuth();
    const [month, setMonth] = useState(null);
    const [data, setData] = useState(null);

    useEffect(() => { if (isPro) api.get("/compare", { params: month ? { month } : {} }).then((r) => setData(r.data)).catch(() => setData({ empty: true })); }, [isPro, month]);

    const shell = (children, actions) => <AppShell title="Compare months" subtitle="This month vs your usual" testid="compare-page" actions={actions}>{children}</AppShell>;
    if (!isPro) return shell(<ProGate feature="Month-on-month comparison" />);
    if (!data) return shell(<Spinner />);
    if (data.empty || !data.summary?.months?.length) return shell(<EmptyData />);

    const months = data.summary.months;
    const cur = data.month;
    const chart = months.map((m) => ({ name: m.label.split(" ")[0].slice(0, 3), spend: Math.round(m.spend), month: m.month }));

    return shell(
        <>
            <section className="rounded-2xl border border-border bg-card p-6" data-testid="compare-chart">
                <div className="flex flex-wrap items-end justify-between gap-3">
                    <div>
                        <p className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground">Total spend by month</p>
                        <p className="mt-1 font-heading text-3xl font-black text-foreground">{gbp(cur.spend, 0)} <span className="text-base font-semibold text-muted-foreground">in {cur.label}</span></p>
                        {data.baseline_spend != null && (
                            <p className={`mt-1 flex items-center gap-1 text-sm font-semibold ${data.diff > 0 ? "text-red-400" : "text-emerald-400"}`} data-testid="compare-diff">
                                {data.diff > 0 ? <ArrowUpRight className="h-4 w-4" /> : <ArrowDownRight className="h-4 w-4" />}{signedGbp(data.diff)} vs your usual {gbp(data.baseline_spend, 0)} <span className="font-normal text-muted-foreground">(avg of {data.baseline_months.join(", ")})</span>
                            </p>
                        )}
                    </div>
                </div>
                <div className="mt-4 h-48">
                    <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={chart} margin={{ left: -10, right: 0, top: 4, bottom: 0 }}>
                            <XAxis dataKey="name" tick={{ fill: "#8EA39C", fontSize: 12 }} axisLine={false} tickLine={false} />
                            <YAxis tick={{ fill: "#8EA39C", fontSize: 11 }} axisLine={false} tickLine={false} tickFormatter={(v) => `£${v}`} />
                            <Tooltip cursor={{ fill: "rgba(16,185,129,0.08)" }} contentStyle={{ background: "#122620", border: "1px solid #1D3B32", borderRadius: 12, color: "#F2F7F5" }} formatter={(v) => [gbp(v, 0), "Spent"]} />
                            <Bar dataKey="spend" radius={[8, 8, 0, 0]} onClick={(d) => setMonth(d.month)} cursor="pointer">
                                {chart.map((c) => <Cell key={c.month} fill={c.month === cur.month ? "#10B981" : "#1E3B33"} />)}
                            </Bar>
                        </BarChart>
                    </ResponsiveContainer>
                </div>
            </section>

            {data.category_diffs?.length > 0 ? (
                <section className="mt-6 overflow-hidden rounded-2xl border border-border bg-card" data-testid="compare-table">
                    <div className="grid grid-cols-[1fr_auto_auto_auto] gap-x-4 border-b border-border px-5 py-2.5 font-mono text-[10px] uppercase tracking-wider text-muted-foreground">
                        <span>Category</span><span className="text-right">This month</span><span className="text-right">Usual</span><span className="w-20 text-right">Change</span>
                    </div>
                    <ul className="divide-y divide-border">
                        {data.category_diffs.map((d, i) => (
                            <li key={d.category} className="grid grid-cols-[1fr_auto_auto_auto] items-center gap-x-4 px-5 py-3 text-sm" data-testid={`compare-row-${i}`}>
                                <span className="flex items-center gap-2 truncate text-foreground/90"><span className="h-2.5 w-2.5 shrink-0 rounded-full" style={{ background: catColor(d.category) }} />{d.category}</span>
                                <span className="font-mono font-semibold text-foreground">{gbp(d.current, 0)}</span>
                                <span className="font-mono text-muted-foreground">{gbp(d.baseline, 0)}</span>
                                <span className={`flex w-20 items-center justify-end gap-0.5 font-mono font-semibold ${Math.abs(d.diff) < 1 ? "text-muted-foreground" : d.diff > 0 ? "text-red-400" : "text-emerald-400"}`}>
                                    {Math.abs(d.diff) < 1 ? <Minus className="h-3 w-3" /> : d.diff > 0 ? <ArrowUpRight className="h-3.5 w-3.5" /> : <ArrowDownRight className="h-3.5 w-3.5" />}{Math.abs(d.diff) < 1 ? "" : signedGbp(d.diff)}
                                </span>
                            </li>
                        ))}
                    </ul>
                </section>
            ) : (
                <p className="mt-6 rounded-2xl border border-border bg-card p-6 text-sm text-muted-foreground" data-testid="compare-need-more">Upload at least two months of statements to compare against your usual spending.</p>
            )}
        </>,
        <MonthPicker months={months} value={cur.month} onChange={setMonth} />
    );
}
